import React, { useState, useMemo } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import ExcelJS from "exceljs";
import { ArrowUpRight, ArrowDownRight, Loader2, Download, FileSpreadsheet, ArrowLeft, Filter, X, Trash2 } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useCurrency } from "@/context/CurrencyContext";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const COLS = ["Fecha", "Tipo", "Categoría", "Cuenta", "Responsable", "Factura", "Monto (MXN)", "Nota"];

export default function Movements() {
  const navigate = useNavigate();
  const { format } = useCurrency();
  const { user } = useAuth();
  const canWrite = user?.role !== "viewer";
  const qc = useQueryClient();
  const { data = [], isLoading } = useQuery({ queryKey: ["all-movements"], queryFn: async () => (await api.get("/movements?limit=2000")).data });
  const { data: accounts = [] } = useQuery({ queryKey: ["accounts"], queryFn: async () => (await api.get("/accounts")).data });
  const { data: members = [] } = useQuery({ queryKey: ["members"], queryFn: async () => (await api.get("/vaults/members")).data });
  const isShared = members.filter((m) => m.status !== "pending").length > 1;
  const accName = (id) => accounts.find((a) => a.id === id)?.name || "";

  const del = async (m) => {
    if (!window.confirm(`¿Eliminar este movimiento de ${m.category}?`)) return;
    try { await api.delete(`/movements/${m.id}`); toast.success("Movimiento eliminado"); qc.invalidateQueries(); }
    catch { toast.error("No se pudo eliminar"); }
  };

  const [type, setType] = useState("all");
  const [account, setAccount] = useState("all");
  const [category, setCategory] = useState("all");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");

  const categories = useMemo(() => Array.from(new Set(data.map((m) => m.category))).sort(), [data]);

  const filtered = useMemo(() => data.filter((m) => {
    if (type !== "all" && m.type !== type) return false;
    if (account !== "all" && m.account_id !== account) return false;
    if (category !== "all" && m.category !== category) return false;
    if (from && (m.movement_date || "") < from) return false;
    if (to && (m.movement_date || "") > to) return false;
    return true;
  }), [data, type, account, category, from, to]);

  const hasFilters = type !== "all" || account !== "all" || category !== "all" || from || to;
  const clearFilters = () => { setType("all"); setAccount("all"); setCategory("all"); setFrom(""); setTo(""); };

  const rowObjects = filtered.map((m) => ({
    fecha: m.movement_date, tipo: m.type === "by" ? "By" : "To", categoria: m.category,
    cuenta: accName(m.account_id), responsable: m.responsible || "",
    factura: m.requires_invoice ? (m.invoice_status || "pendiente") : "",
    monto: m.type === "by" ? m.amount : -m.amount, nota: m.note || "",
  }));

  const downloadBlob = (blob, filename) => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = filename; a.click();
    URL.revokeObjectURL(url);
  };
  const stamp = new Date().toISOString().slice(0, 10);

  const exportCSV = () => {
    const esc = (v) => `"${String(v).replace(/"/g, '""')}"`;
    const rows = rowObjects.map((r) => [r.fecha, r.tipo, r.categoria, r.cuenta, r.responsable, r.factura, r.monto, r.nota]);
    const csv = "\uFEFF" + [COLS, ...rows].map((r) => r.map(esc).join(",")).join("\n");
    downloadBlob(new Blob([csv], { type: "text/csv;charset=utf-8" }), `byto-movimientos-${stamp}.csv`);
  };

  const exportXLSX = async () => {
    const wb = new ExcelJS.Workbook();
    wb.creator = "Byto";
    const ws = wb.addWorksheet("Movimientos", { views: [{ state: "frozen", ySplit: 1 }] });
    ws.columns = [
      { header: "Fecha", key: "fecha", width: 13 },
      { header: "Tipo", key: "tipo", width: 8 },
      { header: "Categoría", key: "categoria", width: 30 },
      { header: "Cuenta", key: "cuenta", width: 22 },
      { header: "Responsable", key: "responsable", width: 18 },
      { header: "Factura", key: "factura", width: 14 },
      { header: "Monto (MXN)", key: "monto", width: 16 },
      { header: "Nota", key: "nota", width: 30 },
    ];
    ws.addRows(rowObjects);
    const header = ws.getRow(1);
    header.font = { bold: true, color: { argb: "FFFFFFFF" } };
    header.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FF059669" } };
    header.alignment = { vertical: "middle" };
    header.height = 20;
    ws.getColumn("monto").numFmt = '"$"#,##0.00';
    ws.autoFilter = "A1:H1";
    ws.eachRow((row, n) => {
      if (n === 1) return;
      const c = row.getCell("monto");
      c.font = { color: { argb: (c.value || 0) < 0 ? "FFE11D48" : "FF059669" } };
    });
    const buf = await wb.xlsx.writeBuffer();
    downloadBlob(new Blob([buf], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }), `byto-movimientos-${stamp}.xlsx`);
  };

  return (
    <div className="py-4" data-testid="movements-page">
      <button onClick={() => navigate("/dashboard")} className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-1 mb-3"><ArrowLeft className="w-4 h-4" /> Volver a la Bóveda</button>
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-3xl sm:text-4xl font-bold tracking-tight">Todos los movimientos</h1>
          <p className="text-muted-foreground text-sm mt-1">{filtered.length} de {data.length} registro(s){hasFilters ? " (filtrados)" : ""}.</p>
        </div>
        <div className="flex gap-2">
          <Button data-testid="export-csv-button" onClick={exportCSV} variant="outline" className="rounded-xl gap-2" disabled={!filtered.length}><Download className="w-4 h-4" /> CSV</Button>
          <Button data-testid="export-xls-button" onClick={exportXLSX} className="rounded-xl gap-2" disabled={!filtered.length}><FileSpreadsheet className="w-4 h-4" /> Excel</Button>
        </div>
      </div>

      {/* Filters */}
      <div className="mt-5 rounded-2xl border border-border bg-card p-4" data-testid="movements-filters">
        <div className="flex items-center gap-2 mb-3 text-sm font-medium text-muted-foreground"><Filter className="w-4 h-4" /> Filtros</div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-3">
          <Select value={type} onValueChange={setType}>
            <SelectTrigger data-testid="filter-type" className="rounded-xl"><SelectValue placeholder="Tipo" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos los tipos</SelectItem>
              <SelectItem value="by">By (ingresos)</SelectItem>
              <SelectItem value="to">To (gastos)</SelectItem>
            </SelectContent>
          </Select>
          <Select value={account} onValueChange={setAccount}>
            <SelectTrigger data-testid="filter-account" className="rounded-xl"><SelectValue placeholder="Cuenta" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todas las cuentas</SelectItem>
              {accounts.map((a) => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={category} onValueChange={setCategory}>
            <SelectTrigger data-testid="filter-category" className="rounded-xl"><SelectValue placeholder="Categoría" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todas las categorías</SelectItem>
              {categories.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
            </SelectContent>
          </Select>
          <Input data-testid="filter-from" type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="rounded-xl" placeholder="Desde" />
          <Input data-testid="filter-to" type="date" value={to} onChange={(e) => setTo(e.target.value)} className="rounded-xl" placeholder="Hasta" />
        </div>
        {hasFilters && (
          <button data-testid="clear-filters" onClick={clearFilters} className="mt-3 text-xs text-rose-500 flex items-center gap-1 hover:underline"><X className="w-3.5 h-3.5" /> Limpiar filtros</button>
        )}
      </div>

      <div className="mt-4 rounded-2xl border border-border bg-card overflow-hidden">
        {isLoading ? (
          <div className="h-40 flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-emerald-500" /></div>
        ) : filtered.length === 0 ? (
          <p className="text-sm text-muted-foreground py-12 text-center">Sin movimientos que coincidan.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="movements-table">
              <thead>
                <tr className="border-b border-border bg-secondary/40 text-left">
                  {COLS.map((c) => <th key={c} className="px-4 py-3 font-semibold text-muted-foreground whitespace-nowrap">{c}</th>)}
                  <th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((m) => {
                  const isBy = m.type === "by";
                  return (
                    <tr key={m.id} className="border-b border-border last:border-0 hover:bg-accent/30" data-testid={`movement-row-${m.id}`}>
                      <td className="px-4 py-3 font-numeric whitespace-nowrap">{m.movement_date}</td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full ${isBy ? "bg-emerald-500/15 text-emerald-500" : "bg-rose-500/15 text-rose-500"}`}>
                          {isBy ? <ArrowUpRight className="w-3 h-3" /> : <ArrowDownRight className="w-3 h-3" />}{isBy ? "By" : "To"}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        {m.category}
                        {isShared && (m.updated_by_name || m.created_by_name) && (
                          <span className="block text-xs text-muted-foreground/80" data-testid={`author-${m.id}`}>
                            {m.updated_by_name ? `editado por ${m.updated_by_name}` : `por ${m.created_by_name}`}
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-muted-foreground whitespace-nowrap">{accName(m.account_id) || "—"}</td>
                      <td className="px-4 py-3 text-muted-foreground">{m.responsible || "—"}</td>
                      <td className="px-4 py-3">{m.requires_invoice ? <span className="text-xs text-amber-500">{m.invoice_status || "pendiente"}</span> : "—"}</td>
                      <td className={`px-4 py-3 font-numeric font-semibold text-right whitespace-nowrap ${isBy ? "text-emerald-500" : "text-rose-500"}`}>{isBy ? "+" : "−"}{format(m.amount)}</td>
                      <td className="px-4 py-3 text-muted-foreground max-w-[220px] truncate">{m.note || "—"}</td>
                      <td className="px-2 py-3 text-right">
                        {canWrite && (
                          <button data-testid={`delete-movement-${m.id}`} onClick={() => del(m)} title="Eliminar"
                            className="w-8 h-8 rounded-lg hover:bg-rose-500/10 flex items-center justify-center text-muted-foreground hover:text-rose-500">
                            <Trash2 className="w-4 h-4" />
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
